import { useEffect, useRef, useState } from "react";
import { FaArrowRight } from "react-icons/fa6";
import { FiCheckCircle } from "react-icons/fi";
import { Link } from "react-router-dom";
import { toRoutePath } from "../../lib/seo";
import styles from "./Banner.module.css";

const DEFAULT_BANNER_BG = "/banner_img/hero_bg.webp";

const DEFAULT_CENTERED_GRADIENT =
  "radial-gradient(ellipse 80% 60% at 50% 0%, #002a4d 0%, #020817 60%, #000510 100%)";

function getCenteredBackground({ BannerBgImg, color }) {
  if (BannerBgImg) {
    return `url(${BannerBgImg})`;
  }

  if (color) {
    return `linear-gradient(160deg, ${color} 0%, rgba(0, 0, 0, 0.75) 100%)`;
  }

  return DEFAULT_CENTERED_GRADIENT;
}

function getBannerBackground({ BannerBgImg, centered }) {
  const resolvedBannerBg =
    BannerBgImg && BannerBgImg !== "home" && BannerBgImg !== "default"
      ? BannerBgImg
      : DEFAULT_BANNER_BG;

  if (centered) {
    return DEFAULT_CENTERED_GRADIENT;
  }

  if (BannerBgImg === "home") {
    return `url(${resolvedBannerBg})`;
  }

  if (BannerBgImg) {
    return `radial-gradient(ellipse 80% 60% at 50% 0%, #002a4d 0%, #020817 60%, #000510 100%), url(${resolvedBannerBg})`;
  }

  return "radial-gradient(ellipse 80% 60% at 50% 0%, #002a4d 0%, #020817 60%, #000510 100%)";
}

function BannerButton({ btnText, btnLink, className = "" }) {
  if (!btnText || !btnLink) {
    return null;
  }

  return (
    <Link to={toRoutePath(btnLink)} className={`btn ${className}`.trim()}>
      <span>{btnText}</span>
      <FaArrowRight aria-hidden="true" />
    </Link>
  );
}

function HeroPill({ children }) {
  if (!children) {
    return null;
  }

  return (
    <div className={styles.heroPill}>
      <span className={styles.heroPillDot} aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

function HeroChips({ chips = [] }) {
  if (!chips.length) {
    return null;
  }

  return (
    <ul className={styles.heroTrustList} aria-label="Highlights">
      {chips.map((chip) => (
        <li key={chip} className={styles.heroTrustItem}>
          <FiCheckCircle aria-hidden="true" />
          <span>{chip}</span>
        </li>
      ))}
    </ul>
  );
}

function BannerImage({ BannerColImg, heading }) {
  if (!BannerColImg) {
    return null;
  }

  return (
    <div className={styles.visualWrap}>
      <img src={BannerColImg} alt={heading} className={styles.visualImage} />
    </div>
  );
}

export default function BannerComp({
  heading = "",
  subHeading = "",
  paragraph = "",
  btnText = "",
  btnLink = "",
  secondaryBtnText = "",
  secondaryBtnLink = "",
  BannerBgImg = "",
  BannerColImg = "",
  color = "",
  overlay = true,
  colored,
  centered = false,
  hero = false,
  heroVariant = "image",
  heroPoints = [],
  heroBadge = "",
  heroChips = [],
}) {
  const bannerBackground = getBannerBackground({
    BannerBgImg,
    centered,
  });
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  const heroImageRef = useRef(null);
  const resolvedHeroBg =
    BannerBgImg && BannerBgImg !== "home" && BannerBgImg !== "default"
      ? BannerBgImg
      : DEFAULT_BANNER_BG;
  const useDarkHero = hero && heroVariant === "dark";

  useEffect(() => {
    if (!hero || useDarkHero) {
      return;
    }

    if (heroImageRef.current?.complete) {
      setHeroImageLoaded(true);
    } else {
      setHeroImageLoaded(false);
    }
  }, [hero, resolvedHeroBg, useDarkHero]);

  if (!heading) {
    return null;
  }

  if (hero) {
    const heroStyle = {
      backgroundImage: useDarkHero
        ? "linear-gradient(180deg, #020817 0%, #00040b 100%)"
        : bannerBackground,
    };

    return (
      <section
        className={`${styles.banner} ${styles.bannerHero} ${useDarkHero ? styles.bannerHeroDark : ""}`}
        style={heroStyle}
      >
        <div className={styles.heroBackdrop} aria-hidden="true">
          <span className={styles.heroOrbPrimary} data-parallax="0.12" />
          <span className={styles.heroOrbSecondary} data-parallax="-0.16" />
          <span className={styles.heroOrbAccent} data-parallax="0.08" />
          <span className={styles.heroGrid} />
        </div>

        {!useDarkHero ? (
          <>
            <img
              ref={heroImageRef}
              src={resolvedHeroBg}
              alt=""
              className={styles.heroPreloadImage}
              onLoad={() => setHeroImageLoaded(true)}
              onError={() => setHeroImageLoaded(true)}
            />
            {!heroImageLoaded ? (
              <div className={styles.heroLoader} aria-hidden="true">
                <span className={styles.heroLoaderSpinner} />
              </div>
            ) : null}
            <div className={styles.heroOverlay} aria-hidden="true" />
          </>
        ) : (
          <div className={styles.heroOverlay} aria-hidden="true" />
        )}

        <div className={styles.heroFrame}>
          <div
            className={`${styles.heroInner} ${useDarkHero ? styles.heroInnerCentered : ""}`}
          >
            <div
              className={`${styles.heroCopy} ${useDarkHero ? styles.heroCopyCentered : ""}`}
            >
              {useDarkHero ? (
                <div>
                  <HeroPill>{heroBadge || "MODERN HEALTHCARE PLATFORM"}</HeroPill>
                </div>
              ) : null}

              <h1 className={styles.heroHeading}>
                {heading}
              </h1>
              {subHeading ? (
                <h2 className={styles.heroSubHeading}>
                  {subHeading}
                </h2>
              ) : null}

              {paragraph ? (
                <p className={styles.heroParagraph}>
                  {paragraph}
                </p>
              ) : null}

              {useDarkHero ? (
                <div className={styles.heroActionsRow}>
                  {btnText && btnLink ? (
                    <BannerButton btnText={btnText} btnLink={btnLink} />
                  ) : null}
                  {secondaryBtnText && secondaryBtnLink ? (
                    <BannerButton
                      btnText={secondaryBtnText}
                      btnLink={secondaryBtnLink}
                      className="btn-dark-outline"
                    />
                  ) : null}
                </div>
              ) : null}

              {useDarkHero ? (
                <div>
                  <HeroChips
                    chips={
                      heroChips.length
                        ? heroChips
                        : ["HIPAA Ready", "Insurance Friendly", "Patient Focused"]
                    }
                  />
                </div>
              ) : null}

              {heroPoints.length && !useDarkHero ? (
                <ul className={styles.heroPoints}>
                  {heroPoints.map((point) => (
                    <li key={point}>
                      <FiCheckCircle aria-hidden="true" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              {!useDarkHero ? (
                <div className={styles.heroActions}>
                  <BannerButton btnText="Find a Doctor" btnLink="/contact-us" />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (
    !centered &&
    (!paragraph || !btnText || !btnLink || !BannerColImg)
  ) {
    return null;
  }

  if (centered) {
    const centeredStyle = {
      backgroundImage: getCenteredBackground({ BannerBgImg, color }),
    };
    const showOverlay = Boolean(BannerBgImg) && overlay;
    const showColored =
      typeof colored === "boolean" ? colored : !BannerBgImg;

    return (
      <section
        className={`${styles.banner} ${styles.bannerCentered} ${styles.bannerCenteredDark}`}
        style={centeredStyle}
      >
        {showColored && (
          <div className={styles.centeredBackdrop} aria-hidden="true">
            <span className={styles.heroOrbPrimary} data-parallax="0.12" />
            <span className={styles.heroOrbSecondary} data-parallax="-0.16" />
            <span className={styles.heroOrbAccent} data-parallax="0.08" />
            <span className={styles.heroGrid} />
          </div>
        )}
        {showOverlay && (
          <div className={styles.centeredOverlay} aria-hidden="true" />
        )}
        <div className={`container ${styles.centeredInner}`}>
          <div className={styles.centeredContent}>
            <h1>{heading}</h1>
            {subHeading ? (
              <h2 className={styles.centeredSubHeading}>
                {subHeading}
              </h2>
            ) : null}
            {paragraph ? (
              <p>
                {paragraph}
              </p>
            ) : null}
            {btnText && btnLink ? (
              <div className={styles.centeredActions}>
                <BannerButton btnText={btnText} btnLink={btnLink} />
              </div>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  const bannerStyle = {
    backgroundImage: bannerBackground,
  };

  return (
    <section className={styles.banner} style={bannerStyle}>
      <div className={`container ${styles.inner}`}>
        <div className={styles.content}>
          <h1>{heading}</h1>

          {subHeading ? (
            <h2 className={styles.subHeading}>
              {subHeading}
            </h2>
          ) : null}

          <p>
            {paragraph}
          </p>

          <div>
            <BannerButton btnText={btnText} btnLink={btnLink} />
          </div>
        </div>
        <div>
          <BannerImage BannerColImg={BannerColImg} heading={heading} />
        </div>
      </div>
    </section>
  );
}
